import { UseFormReturn } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../ui/components/ui/form";
import { Textarea } from "../../../ui/components/ui/textarea";
import { IDENTITY_CHAR_LIMITS, OnboardingFormData } from "../onboarding-schema";

interface IdentityStepProps {
  form: UseFormReturn<OnboardingFormData>;
}

const CharCounter = ({ current, max }: { current: number; max: number }) => {
  if (current === 0) return null;
  const isNearLimit = current > max * 0.9;
  const isOver = current > max;
  return (
    <p
      className={`text-xs text-right mt-1 ${
        isOver ? "text-red-500 font-medium" : isNearLimit ? "text-amber-500" : "text-gray-400"
      }`}
    >
      {current} / {max}
    </p>
  );
};

const IdentityStep = ({ form }: IdentityStepProps) => {
  const aboutUs = form.watch("aboutUs") ?? "";
  const missionAndValues = form.watch("missionAndValues") ?? "";
  const whyWorkWithUs = form.watch("whyWorkWithUs") ?? "";

  return (
    <div>
      <p className="text-sm font-medium mb-1" style={{ color: "#0099ce" }}>
        Schritt 7 von 9
      </p>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">
        Zeigen Sie, wofür Ihr Unternehmen steht.
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        Diese Texte bilden die Grundlage Ihrer Arbeitgebermarke und werden in
        Anzeigen, auf Ihrer Karriereseite und im Bewerbungsprozess verwendet.
      </p>

      <div className="space-y-6">
        {/* Section 1: Über uns */}
        <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-8">
          <h2 className="text-base font-semibold text-gray-900 mb-1">
            Über uns
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            Dieser Text bildet die Basis der Arbeitgeberdarstellung. Er zeigt
            Bewerbern, wofür Ihr Unternehmen steht, und wird u.a. für den
            Bewerbungsprozess und Anzeigen genutzt.
          </p>

          <FormField
            control={form.control}
            name="aboutUs"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Unternehmensbeschreibung{" "}
                  <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    rows={6}
                    maxLength={IDENTITY_CHAR_LIMITS.aboutUs}
                    placeholder="Beschreiben Sie Ihr Unternehmen in 5–10 Sätzen. Was macht Sie aus? Seit wann gibt es Sie? Was ist Ihre Mission?"
                    className="mt-1 resize-y"
                  />
                </FormControl>
                <div className="flex justify-between items-start">
                  <FormMessage />
                  <CharCounter
                    current={aboutUs.length}
                    max={IDENTITY_CHAR_LIMITS.aboutUs}
                  />
                </div>
              </FormItem>
            )}
          />
        </div>

        {/* Section 2: Leitbild, Werte & Kultur */}
        <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-8">
          <h2 className="text-base font-semibold text-gray-900 mb-1">
            Leitbild, Werte & Kultur
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            Klare Werte helfen uns dabei, Kandidaten anzusprechen, die
            langfristig zu Ihrem Team passen.
          </p>

          <FormField
            control={form.control}
            name="missionAndValues"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Werte & Kultur <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    rows={5}
                    maxLength={IDENTITY_CHAR_LIMITS.missionAndValues}
                    placeholder="Welche Werte, Haltungen und Überzeugungen prägen Ihr Unternehmen? Was ist Ihnen im Umgang miteinander wichtig?"
                    className="mt-1 resize-y"
                  />
                </FormControl>
                <div className="flex justify-between items-start">
                  <FormMessage />
                  <CharCounter
                    current={missionAndValues.length}
                    max={IDENTITY_CHAR_LIMITS.missionAndValues}
                  />
                </div>
              </FormItem>
            )}
          />
        </div>

        {/* Section 3: Warum bei Ihnen arbeiten? */}
        <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-8">
          <h2 className="text-base font-semibold text-gray-900 mb-1">
            Warum arbeitet man gerne bei Ihnen?
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            Hier geht es um authentische Einblicke. Aussagen der Mitarbeiter
            haben eine besonders starke Wirkung auf Bewerber.
          </p>

          <FormField
            control={form.control}
            name="whyWorkWithUs"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Gründe <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    rows={5}
                    maxLength={IDENTITY_CHAR_LIMITS.whyWorkWithUs}
                    placeholder="Was sagen Ihre bestehenden Mitarbeiter: Warum arbeitet man gerne bei Ihnen? Was schätzen sie besonders?"
                    className="mt-1 resize-y"
                  />
                </FormControl>
                <div className="flex justify-between items-start">
                  <FormMessage />
                  <CharCounter
                    current={whyWorkWithUs.length}
                    max={IDENTITY_CHAR_LIMITS.whyWorkWithUs}
                  />
                </div>
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default IdentityStep;
